import { useEffect, useState } from "react";
import { ArrowUpRight, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blankArticle = { id: "", title: "", platform: "Medium", url: "", publicationDate: "", readingTime: "", excerpt: "", topicTags: "", ctaLabel: "Read on Medium", published: true, order: 0 };

function fromArticle(article) {
  return {
    id: article.id,
    title: article.title,
    platform: article.platform,
    url: article.url,
    publicationDate: article.publication_date || article.publicationDate,
    readingTime: article.reading_time || article.readingTime,
    excerpt: article.excerpt,
    topicTags: (article.topic_tags || article.topicTags || []).join(", "),
    ctaLabel: article.cta_label || article.ctaLabel || "Read Article",
    published: article.published !== false,
    order: article.order || 0,
  };
}

function articlePayload(form) {
  return {
    title: form.title,
    platform: form.platform,
    url: form.url,
    publicationDate: form.publicationDate,
    readingTime: form.readingTime,
    excerpt: form.excerpt,
    topicTags: form.topicTags.split(",").map((tag) => tag.trim()).filter(Boolean),
    ctaLabel: form.ctaLabel,
    published: form.published,
    order: Number(form.order) || 0,
  };
}

export default function ExternalArticlesManager() {
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState({ ...blankArticle });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`${API}/external-articles/manage`, { withCredentials: true }).then(({ data }) => setArticles(data)).catch((error) => setStatus({ type: "error", message: formatApiErrorDetail(error.response?.data?.detail) })).finally(() => setLoading(false));
  }, []);

  const update = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setStatus({ type: "", message: "" });
  };

  const reset = () => {
    setForm({ ...blankArticle });
    setStatus({ type: "", message: "" });
  };

  const edit = (article) => {
    setForm(fromArticle(article));
    setStatus({ type: "", message: "" });
  };

  const save = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });
    try {
      const request = form.id ? api.put(`${API}/external-articles/${form.id}`, articlePayload(form), { withCredentials: true }) : api.post(`${API}/external-articles`, articlePayload(form), { withCredentials: true });
      const { data } = await request;
      setArticles((current) => form.id ? current.map((item) => item.id === data.id ? data : item) : [data, ...current]);
      setForm(fromArticle(data));
      setStatus({ type: "success", message: "The external article is saved." });
    } catch (error) {
      setStatus({ type: "error", message: formatApiErrorDetail(error.response?.data?.detail) });
    }
  };

  const remove = async (article) => {
    if (!window.confirm("Remove this external article from the publication list?")) return;
    try {
      await api.delete(`${API}/external-articles/${article.id}`, { withCredentials: true });
      setArticles((current) => current.filter((item) => item.id !== article.id));
      if (form.id === article.id) reset();
      setStatus({ type: "success", message: "The external article is removed." });
    } catch (error) {
      setStatus({ type: "error", message: formatApiErrorDetail(error.response?.data?.detail) });
    }
  };

  return (
    <section className="editor-extension-panel external-articles-manager" data-testid="editor-external-articles-section">
      <div className="editor-extension-head"><div><span className="section-kicker">Published elsewhere / external articles</span><h2>Keep the public trail current.</h2></div><button type="button" className="editor-new" onClick={reset} data-testid="editor-external-new-button"><Plus size={15} /> New article</button></div>
      <p className="editor-extension-copy">Add links to essays published on Medium, Substack, LinkedIn, or another approved publication. These entries stay separate from the owned Insights notes.</p>
      <form className="editor-extension-form" onSubmit={save} data-testid="editor-external-article-form">
        <div className="editor-fields">
          <label className="form-field"><span className="form-label">Title</span><input className="form-input" name="title" value={form.title} onChange={update} required data-testid="editor-external-title-input" /></label>
          <label className="form-field"><span className="form-label">Platform</span><input className="form-input" name="platform" value={form.platform} onChange={update} required data-testid="editor-external-platform-input" /></label>
          <label className="form-field full"><span className="form-label">Published URL</span><input className="form-input" type="url" name="url" value={form.url} onChange={update} placeholder="https://medium.com/..." required data-testid="editor-external-url-input" /></label>
          <label className="form-field"><span className="form-label">Publication date</span><input className="form-input" name="publicationDate" value={form.publicationDate} onChange={update} placeholder="Sep 2026" required data-testid="editor-external-date-input" /></label>
          <label className="form-field"><span className="form-label">Read time</span><input className="form-input" name="readingTime" value={form.readingTime} onChange={update} placeholder="5 min read" required data-testid="editor-external-read-time-input" /></label>
          <label className="form-field full"><span className="form-label">Short excerpt</span><textarea className="form-textarea editor-short-text" name="excerpt" value={form.excerpt} onChange={update} required data-testid="editor-external-excerpt-input" /></label>
          <label className="form-field full"><span className="form-label">Topic tags</span><input className="form-input" name="topicTags" value={form.topicTags} onChange={update} placeholder="Data & Analytics, Decision Intelligence" data-testid="editor-external-tags-input" /></label>
          <label className="form-field"><span className="form-label">Link label</span><input className="form-input" name="ctaLabel" value={form.ctaLabel} onChange={update} required data-testid="editor-external-cta-input" /></label>
          <label className="form-field"><span className="form-label">Display order</span><input className="form-input" type="number" min="0" name="order" value={form.order} onChange={update} data-testid="editor-external-order-input" /></label>
        </div>
        <div className="editor-actions"><label className="editor-publish"><input type="checkbox" name="published" checked={form.published} onChange={update} data-testid="editor-external-published-checkbox" /> Show publicly</label><button type="submit" className="button-primary" data-testid="editor-external-save-button"><Save size={15} /> {form.id ? "Update article" : "Save article"}</button>{form.id && <button type="button" className="editor-delete" onClick={reset} data-testid="editor-external-cancel-button">Cancel</button>}<span className={`editor-status ${status.type}`} role="status" data-testid="editor-external-status-message">{status.message}</span></div>
      </form>
      <div className="external-articles-manager-list" data-testid="editor-external-article-list">
        <div className="evidence-drafts-head"><span className="section-kicker">Publication list</span><span>{loading ? "Loading…" : `${articles.length} ${articles.length === 1 ? "link" : "links"}`}</span></div>
        {!loading && articles.length === 0 ? <p className="editor-muted">No external articles yet. Add the first published link above.</p> : articles.map((article) => <article className="external-articles-manager-item" key={article.id} data-testid={`editor-external-item-${article.id}`}><div><span className="card-index">{article.platform} · {article.publication_date || article.publicationDate}</span><h3>{article.title}</h3><p>{article.excerpt}</p></div><div className="external-articles-manager-actions"><span className={`editor-extension-badge ${article.published ? "approved" : "pending"}`}>{article.published ? "Public" : "Hidden"}</span><a href={article.url} target="_blank" rel="noreferrer noopener" className="editor-quiet-action" data-testid={`editor-external-preview-${article.id}`} aria-label={`Open ${article.title} (opens in a new tab)`}>View <ArrowUpRight size={13} /></a><button type="button" className="editor-quiet-action" onClick={() => edit(article)} data-testid={`editor-external-edit-${article.id}`}><Pencil size={13} /> Edit</button><button type="button" className="editor-delete" onClick={() => remove(article)} data-testid={`editor-external-delete-${article.id}`}><Trash2 size={13} /> Delete</button></div></article>)}
      </div>
    </section>
  );
}
